import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationCard = ({ notification, onMarkAsRead, onClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getTypeConfig = (type) => {
    switch (type) {
      case 'quote':
        return {
          icon: 'FileText',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          label: 'Quote'
        };
      case 'milestone':
        return {
          icon: 'CheckCircle',
          color: 'text-success',
          bgColor: 'bg-success/10',
          borderColor: 'border-success/20',
          label: 'Milestone'
        };
      case 'message':
        return {
          icon: 'MessageCircle',
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning/20',
          label: 'Message'
        };
      case 'vendor':
        return {
          icon: 'Users',
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200',
          label: 'Vendor'
        };
      default:
        return {
          icon: 'Bell',
          color: 'text-text-secondary',
          bgColor: 'bg-muted',
          borderColor: 'border-border',
          label: 'Notification'
        };
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-error/10 text-error border-error/20';
      case 'medium':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'low':
        return 'bg-muted text-text-secondary border-border';
      default:
        return 'bg-muted text-text-secondary border-border';
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffMs = now - notificationTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return notificationTime?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const typeConfig = getTypeConfig(notification?.type);

  const handleCardClick = (e) => {
    if (e?.target?.closest('button')) return;
    onClick?.(notification);
  };

  const handleToggleExpand = (e) => {
    e?.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleMarkAsReadClick = (e) => {
    e?.stopPropagation();
    onMarkAsRead?.(notification?.id);
  };

  return (
    <div
      onClick={handleCardClick}
      className={`bg-card border rounded-lg p-4 transition-all duration-200 cursor-pointer ${
        notification?.isRead
          ? 'border-border hover:shadow-card'
          : `${typeConfig?.borderColor} border-l-4 hover:shadow-lg`
      }`}
    >
      <div className="flex items-start space-x-4">
        {/* Icon */}
        <div className={`w-10 h-10 ${typeConfig?.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon name={typeConfig?.icon} size={20} className={typeConfig?.color} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className={`font-semibold text-foreground ${
                  notification?.isRead ? 'font-medium' : 'font-semibold'
                }`}>
                  {notification?.title}
                </h3>
                {!notification?.isRead && (
                  <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                )}
              </div>
              <div className="flex items-center space-x-2 text-xs text-text-secondary">
                <span className={`px-2 py-0.5 rounded border ${getPriorityBadge(notification?.priority)}`}>
                  {notification?.priority}
                </span>
                <span className={`px-2 py-0.5 rounded ${typeConfig?.bgColor} ${typeConfig?.color}`}>
                  {typeConfig?.label}
                </span>
                <span>{getTimeAgo(notification?.timestamp)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2 ml-4">
              {!notification?.isRead && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleMarkAsReadClick}
                  className="h-8 w-8"
                  title="Mark as read"
                >
                  <Icon name="Check" size={16} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleExpand}
                className="h-8 w-8"
              >
                <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
              </Button>
            </div>
          </div>

          {/* Message */}
          <p className="text-sm text-text-secondary mb-2">{notification?.message}</p>

          {/* Project Info */}
          <div className="flex items-center space-x-4 text-xs text-text-secondary">
            <div className="flex items-center space-x-1">
              <Icon name="FolderOpen" size={14} />
              <span>{notification?.projectName}</span>
            </div>
            {notification?.vendorName && (
              <div className="flex items-center space-x-1">
                <Icon name="User" size={14} />
                <span>{notification?.vendorName}</span>
              </div>
            )}
            {notification?.amount && (
              <div className="flex items-center space-x-1">
                <Icon name="DollarSign" size={14} />
                <span>${notification?.amount?.toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Expanded Details */}
          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-border animate-slide-up">
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Project ID:</span>
                  <span className="text-foreground font-medium">#{notification?.projectId}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Timestamp:</span>
                  <span className="text-foreground">
                    {new Date(notification?.timestamp)?.toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Status:</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    notification?.isRead
                      ? 'bg-muted text-text-secondary' :'bg-primary/10 text-primary'
                  }`}>
                    {notification?.isRead ? 'Read' : 'Unread'}
                  </span>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-4 flex items-center space-x-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => onClick?.(notification)}
                  iconName="ArrowRight"
                  iconPosition="right"
                  iconSize={14}
                >
                  View Details
                </Button>
                {!notification?.isRead && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleMarkAsReadClick}
                    iconName="Check"
                    iconSize={14}
                  >
                    Mark as Read
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;